import React, { useEffect, useState, useMemo, useContext } from 'react';
import Layout from 'components/sales/layout/layout';
import { AppliedFiltersBar, TableSectionV2 } from 'components/UI';
import localization from 'assets/localization/localization';
import Images from 'assets/images/imageData';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import { MobileActionMenu, DesktopActionMenu } from 'components/layout';
import ListFilters from 'components/sales/ListFilters/ListFilters';
import './vendorstyle.css';

const INITIAL_FILTERS = { searchKey: '' };

const cell = (val) => (val != null && val !== '' ? String(val) : '—');

const VendorList = ({
  getVendorRequestList,
  getVendorRequestListSearch,
  postSalesList,
}) => {
  const { baseUrl } = useContext(BaseUrl);
  const [listData, setListData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterData, setFilterData] = useState(INITIAL_FILTERS);
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    if (getVendorRequestList !== null) {
      (async () => {
        setIsLoading(true);
        await getVendorRequestList(null, 1);
        setIsLoading(false);
      })();
    }
  }, [getVendorRequestList]);

  useEffect(() => {
    if (postSalesList?.data) {
      setListData(postSalesList.data);
      setPageCount(Math.ceil(postSalesList.total / postSalesList.per_page));
      if (postSalesList.current_page) {
        setCurrentPage(postSalesList.current_page);
      }
    } else {
      setListData([]);
      setPageCount(0);
    }
  }, [postSalesList]);

  const tableColumns = useMemo(
    () => [
      { key: 'vendor_name', label: 'Vendor Name', format: (val) => cell(val) },
      { key: 'name_of_firm', label: 'Firm Name', format: (val) => cell(val) },
      { key: 'email', label: 'Mail Id', format: (val) => cell(val) },
      { key: 'vendor_group', label: 'Vendor Group', format: (val) => cell(val) },
      { key: 'created_at', label: 'Requested Date', format: (val) => cell(val) },
    ],
    []
  );

  const appliedFilterLabels = useMemo(() => {
    const labels = [];
    const searchKey = filterData?.searchKey?.trim();
    if (searchKey) labels.push(`Vendor / Firm: ${searchKey}`);
    return labels;
  }, [filterData]);

  const filterToggle = () => {
    setShowFilter(!showFilter);
  };

  const fetchList = async (page, filters = filterData) => {
    const searchKey = filters?.searchKey?.trim();
    if (searchKey) {
      await getVendorRequestListSearch(searchKey, page);
    } else {
      await getVendorRequestList(null, page);
    }
  };

  const headerBtns = () => {
    const actionsMenuItems = [
      {
        label: 'Add Vendor',
        to: `${baseUrl}/add_vendor_req`,
        icon: Images?.AddUser,
      },
      {
        label: localization.showFilters,
        onClick: filterToggle,
        icon: Images?.FilterW,
      },
    ];

    return (
      <div className="d-flex align-items-center flex-wrap gap-2 vendor-listing-header-actions">
        <MobileActionMenu items={actionsMenuItems} toggleLabel="Actions" />
        <DesktopActionMenu items={actionsMenuItems} toggleLabel="Actions" />
      </div>
    );
  };

  const applyFilter = async (appliedFilterData) => {
    setFilterData(appliedFilterData);
    setIsLoading(true);
    setCurrentPage(1);
    await fetchList(1, appliedFilterData);
    setIsLoading(false);
    filterToggle();
  };

  const removeFilter = async () => {
    setFilterData(INITIAL_FILTERS);
    setIsLoading(true);
    setCurrentPage(1);
    await getVendorRequestList(null, 1);
    setIsLoading(false);
  };

  const onPageChange = async (val) => {
    const page = val.selected + 1;
    setCurrentPage(page);
    setIsLoading(true);
    await fetchList(page);
    setIsLoading(false);
  };

  const srNo = postSalesList?.from ?? (currentPage - 1) * (postSalesList?.per_page || 10) + 1;

  return (
    <Layout
      className="vendor-listing-page"
      title={localization.vendorReq}
      Buttons={headerBtns}
      isLoading={isLoading}
      isGoback
      linkFlag
      linkUrl={`${baseUrl}/dashboard`}
      useNewHeaderLayout
    >
      <AppliedFiltersBar
        items={appliedFilterLabels}
        onClear={removeFilter}
        clearButtonLabel="Remove filter"
        clearButtonDisabled={isLoading}
      />

      <TableSectionV2
        hideHeader
        hideTitleAndStatusFilter
        className="vendor-listing-table-section"
        tableData={listData}
        tableColumns={tableColumns}
        tableSectionProps={{ srNo }}
        pageCount={pageCount}
        currentPage={currentPage}
        onPageChange={onPageChange}
        isLoading={isLoading}
        emptyMessage="No vendor requests found."
      />

      <ListFilters
        showFilter={showFilter}
        filterToggle={filterToggle}
        applyFilter={(val) => applyFilter(val)}
        vendorListingFilter
        listFilterData={{ appliedFilterValues: filterData }}
      />
    </Layout>
  );
};

export default VendorList;
